import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Star, Video, Wifi, Globe, Heart, Zap, Users, MessageCircle, Package, TrendingUp, Sparkles } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost6Props {
    onBack: () => void;
}

export function BlogPost6({ onBack }: BlogPost6Props) {
    const { t, tObject } = useTranslation();

    const meta = tObject('blogPost6.meta') as any;
    const intro = tObject('blogPost6.intro') as any;
    const section1 = tObject('blogPost6.section1') as any;
    const section2 = tObject('blogPost6.section2') as any;
    const section3 = tObject('blogPost6.section3') as any;
    const section4 = tObject('blogPost6.section4') as any;
    const section5 = tObject('blogPost6.section5') as any;
    const cta = tObject('blogPost6.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            {/* Hero Section */}
            <div className="relative h-[500px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-teal-600 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-blue-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                {/* Content Container */}
                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-blue-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-blue-50 rounded-2xl p-8 mb-12 border border-blue-100">
                        <h3 className="text-blue-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.roadmapTitle}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-3">
                            <ul className="list-none space-y-2 m-0">
                                {intro?.roadmap?.slice(0, 3).map((item: string, i: number) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <ul className="list-none space-y-2 m-0">
                                {intro?.roadmap?.slice(3).map((item: string, i: number) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Seção 1 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-teal-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-teal-600 text-white rounded-xl text-2xl font-bold">1</span>
                            {section1?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section1?.intro}</p>

                        <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-xl mb-8 border border-blue-100">
                            <h4 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-5 h-5" />
                                {section1?.whyTitle}
                            </h4>
                            <ul className="space-y-4 text-gray-700">
                                <li className="flex items-start gap-3">
                                    <Globe className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong>{section1?.reasons?.global?.title}</strong> {section1?.reasons?.global?.description}
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Zap className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong>{section1?.reasons?.practical?.title}</strong> {section1?.reasons?.practical?.description}
                                    </div>
                                </li>
                                <li className="flex items-start gap-3">
                                    <Users className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong>{section1?.reasons?.scalable?.title}</strong> {section1?.reasons?.scalable?.description}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Seção 2 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-purple-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-purple-600 text-white rounded-xl text-2xl font-bold">2</span>
                            {section2?.title}
                        </h2>

                        <div className="space-y-8">
                            <div className="bg-white border-2 border-purple-100 rounded-xl p-8 hover:shadow-lg transition-shadow">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center font-bold text-xl text-purple-600">1</div>
                                    <h4 className="font-bold text-xl">{section2?.step1?.title}</h4>
                                </div>
                                <p className="text-gray-600 ml-16 leading-relaxed">{section2?.step1?.description}</p>
                            </div>

                            <div className="bg-white border-2 border-purple-100 rounded-xl p-8 hover:shadow-lg transition-shadow">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center font-bold text-xl text-purple-600">2</div>
                                    <h4 className="font-bold text-xl">{section2?.step2?.title}</h4>
                                </div>
                                <p className="text-gray-600 ml-16 mb-4 leading-relaxed">{section2?.step2?.intro}</p>
                                <ul className="ml-16 space-y-3 text-gray-600">
                                    <li>📦 <strong>{section2?.step2?.options?.physical?.split(':')[0]}:</strong> {section2?.step2?.options?.physical?.split(':').slice(1).join(':')}</li>
                                    <li>💳 <strong>{section2?.step2?.options?.giftCard?.split(':')[0]}:</strong> {section2?.step2?.options?.giftCard?.split(':').slice(1).join(':')}</li>
                                    <li>🎨 <strong>{section2?.step2?.options?.experience?.split(':')[0]}:</strong> {section2?.step2?.options?.experience?.split(':').slice(1).join(':')}</li>
                                    <li>🎁 <strong>{section2?.step2?.options?.hybrid?.split(':')[0]}:</strong> {section2?.step2?.options?.hybrid?.split(':').slice(1).join(':')}</li>
                                </ul>
                            </div>

                            <div className="bg-white border-2 border-purple-100 rounded-xl p-8 hover:shadow-lg transition-shadow">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center font-bold text-xl text-purple-600">3</div>
                                    <h4 className="font-bold text-xl">{section2?.step3?.title}</h4>
                                </div>
                                <p className="text-gray-600 ml-16 leading-relaxed">{section2?.step3?.description}</p>
                            </div>
                        </div>
                    </section>

                    {/* Seção 3 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-green-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-green-600 text-white rounded-xl text-2xl font-bold">3</span>
                            {section3?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section3?.intro}</p>

                        <div className="grid md:grid-cols-2 gap-8 mb-8">
                            <div className="bg-green-50 p-8 rounded-xl border border-green-100 hover:bg-green-100 transition-colors">
                                <h4 className="font-bold text-green-700 mb-4 flex items-center gap-2 text-lg">
                                    <Package className="w-6 h-6" />
                                    {section3?.options?.ecommerce?.title}
                                </h4>
                                <p className="text-base text-gray-700 mb-4 leading-relaxed">{section3?.options?.ecommerce?.description}</p>
                                <p className="text-sm text-green-600 font-bold uppercase tracking-wide">{section3?.options?.ecommerce?.badge}</p>
                            </div>

                            <div className="bg-blue-50 p-8 rounded-xl border border-blue-100 hover:bg-blue-100 transition-colors">
                                <h4 className="font-bold text-blue-700 mb-4 flex items-center gap-2 text-lg">
                                    <Gift className="w-6 h-6" />
                                    {section3?.options?.giftCard?.title}
                                </h4>
                                <p className="text-base text-gray-700 mb-4 leading-relaxed">{section3?.options?.giftCard?.description}</p>
                                <p className="text-sm text-blue-600 font-bold uppercase tracking-wide">{section3?.options?.giftCard?.badge}</p>
                            </div>

                            <div className="bg-yellow-50 p-8 rounded-xl border border-yellow-100 hover:bg-yellow-100 transition-colors">
                                <h4 className="font-bold text-yellow-700 mb-4 flex items-center gap-2 text-lg">
                                    <Sparkles className="w-6 h-6" />
                                    {section3?.options?.creative?.title}
                                </h4>
                                <p className="text-base text-gray-700 mb-4 leading-relaxed">{section3?.options?.creative?.description}</p>
                                <p className="text-sm text-yellow-600 font-bold uppercase tracking-wide">{section3?.options?.creative?.badge}</p>
                            </div>

                            <div className="bg-purple-50 p-8 rounded-xl border border-purple-100 hover:bg-purple-100 transition-colors">
                                <h4 className="font-bold text-purple-700 mb-4 flex items-center gap-2 text-lg">
                                    <Heart className="w-6 h-6" />
                                    {section3?.options?.subscription?.title}
                                </h4>
                                <p className="text-base text-gray-700 mb-4 leading-relaxed">{section3?.options?.subscription?.description}</p>
                                <p className="text-sm text-purple-600 font-bold uppercase tracking-wide">{section3?.options?.subscription?.badge}</p>
                            </div>
                        </div>
                    </section>

                    {/* Seção 4 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-red-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-red-600 text-white rounded-xl text-2xl font-bold">4</span>
                            {section4?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section4?.intro}</p>

                        <div className="space-y-6">
                            <div className="bg-white shadow-lg rounded-xl p-6 border-l-8 border-red-500 hover:translate-x-2 transition-transform">
                                <h4 className="font-bold text-xl mb-3 text-gray-900">{section4?.dynamics?.guessWho?.title}</h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section4?.dynamics?.guessWho?.description}</p>
                            </div>

                            <div className="bg-white shadow-lg rounded-xl p-6 border-l-8 border-blue-500 hover:translate-x-2 transition-transform">
                                <h4 className="font-bold text-xl mb-3 text-gray-900">{section4?.dynamics?.talentShow?.title}</h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section4?.dynamics?.talentShow?.description}</p>
                            </div>

                            <div className="bg-white shadow-lg rounded-xl p-6 border-l-8 border-green-500 hover:translate-x-2 transition-transform">
                                <h4 className="font-bold text-xl mb-3 text-gray-900">{section4?.dynamics?.drawing?.title}</h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section4?.dynamics?.drawing?.description}</p>
                            </div>
                        </div>
                    </section>

                    {/* Seção 5 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-orange-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-orange-500 text-white rounded-xl text-2xl font-bold">5</span>
                            {section5?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section5?.intro}</p>

                        <div className="bg-red-50 border-l-4 border-red-500 p-8 mb-6 rounded-r-xl">
                            <h4 className="font-bold text-red-800 mb-3 text-lg">{section5?.errors?.tech?.title}</h4>
                            <p className="text-gray-700 text-base mb-3 leading-relaxed">{section5?.errors?.tech?.problem}</p>
                            <p className="text-green-700 text-base font-bold bg-white p-3 rounded-lg inline-block border border-green-200">{section5?.errors?.tech?.solution}</p>
                        </div>

                        <div className="bg-red-50 border-l-4 border-red-500 p-8 mb-6 rounded-r-xl">
                            <h4 className="font-bold text-red-800 mb-3 text-lg">{section5?.errors?.timezone?.title}</h4>
                            <p className="text-gray-700 text-base mb-3 leading-relaxed">{section5?.errors?.timezone?.problem}</p>
                            <p className="text-green-700 text-base font-bold bg-white p-3 rounded-lg inline-block border border-green-200">{section5?.errors?.timezone?.solution}</p>
                        </div>

                        <div className="bg-red-50 border-l-4 border-red-500 p-8 mb-6 rounded-r-xl">
                            <h4 className="font-bold text-red-800 mb-3 text-lg">{section5?.errors?.delivery?.title}</h4>
                            <p className="text-gray-700 text-base mb-3 leading-relaxed">{section5?.errors?.delivery?.problem}</p>
                            <p className="text-green-700 text-base font-bold bg-white p-3 rounded-lg inline-block border border-green-200">{section5?.errors?.delivery?.solution}</p>
                        </div>
                    </section>

                    <hr className="my-12 border-gray-200" />

                    {/* CTA Final */}
                    <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-3xl p-8 md:p-16 text-white text-center shadow-2xl relative overflow-hidden transform transition-all hover:scale-[1.01]">
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                        <Video className="w-24 h-24 mx-auto mb-8 text-teal-200 animate-pulse drop-shadow-md" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-8 relative z-10 leading-tight">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10 font-light">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="bg-white text-blue-600 px-12 py-6 rounded-full font-bold text-xl hover:bg-gray-50 hover:shadow-2xl hover:-translate-y-1 transition-all shadow-xl relative z-10 flex items-center gap-4 mx-auto group"
                        >
                            <Wifi className="w-7 h-7 group-hover:scale-110 transition-transform" />
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}